
import React, { useState } from 'react';
import AddWineForm from './AddWineForm';
import WineListTable from './WineListTable';
import type { Wine, AppData, ShopProfile } from '../types';
import TrashIcon from './icons/TrashIcon';
import QRCodeDisplay from './QRCodeDisplay';
import { generateStaticWineListUrl } from '../utils/staticHtmlGenerator';
import { saveStaticWineList } from '../services/firestoreService';

interface SettingsProps {
    onBack: () => void;
    onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onAddWine: (wine: Omit<Wine, 'id'>) => void;
    onRemoveWine: (wineId: number) => void;
    onStartEdit: (wine: Wine) => void;
    onToggleWineVisibility: (wineId: number) => void;
    csvFileName: string | null;
    csvError: string | null;
    wineList: Wine[];
    occasions: string[];
    onAddOccasion: (occasion: string) => void;
    onRemoveOccasion: (occasion: string) => void;
    tastes: string[];
    onAddTaste: (taste: string) => void;
    onRemoveTaste: (taste: string) => void;
    prices: string[];
    onAddPrice: (price: string) => void;
    onRemovePrice: (price: string) => void;
    origins: string[];
    onAddOrigin: (origin: string) => void;
    onRemoveOrigin: (origin: string) => void;
    onImportData: (data: AppData) => Promise<boolean>;
    shopProfile: ShopProfile;
    onUpdateShopProfile: (update: Partial<ShopProfile>) => void;
    userId?: string;
    // ページネーション設定
    paginationSettings: {
        enabled: boolean;
        mode: 'byGenre' | 'byCount';
        winesPerPage: number;
    };
    onUpdatePaginationSettings: (settings: {
        enabled: boolean;
        mode: 'byGenre' | 'byCount';
        winesPerPage: number;
    }) => void;
}

const Settings: React.FC<SettingsProps> = ({ 
    onBack, 
    onFileChange, 
    onAddWine, 
    onRemoveWine, 
    onStartEdit, 
    onToggleWineVisibility, 
    csvFileName, 
    csvError, 
    wineList, 
    occasions, 
    onAddOccasion, 
    onRemoveOccasion, 
    tastes, 
    onAddTaste, 
    onRemoveTaste, 
    prices, 
    onAddPrice, 
    onRemovePrice, 
    origins, 
    onAddOrigin, 
    onRemoveOrigin, 
    onImportData, 
    shopProfile, 
    onUpdateShopProfile, 
    userId,
    paginationSettings,
    onUpdatePaginationSettings
}) => {
    
    const [newOccasion, setNewOccasion] = useState('');
    const [newTaste, setNewTaste] = useState('');
    const [newPrice, setNewPrice] = useState('');
    const [newOrigin, setNewOrigin] = useState('');
    const [importCode, setImportCode] = useState('');
    const [importError, setImportError] = useState<string | null>(null);
    const [importSuccess, setImportSuccess] = useState<string | null>(null);
    const [exportCode, setExportCode] = useState<string | null>(null);
    const [exportMessage, setExportMessage] = useState('');

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const result = reader.result as string;
                onUpdateShopProfile({ logo: result });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveLogo = () => {
        onUpdateShopProfile({ logo: '' });
        // Also clear the file input
        const fileInput = document.getElementById('logo-upload') as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
    };

    const handleAddOccasionClick = () => {
        const trimmedOccasion = newOccasion.trim();
        if (trimmedOccasion) {
            onAddOccasion(trimmedOccasion);
            setNewOccasion('');
        }
    };

    const handleAddTasteClick = () => {
        const trimmedTaste = newTaste.trim();
        if (trimmedTaste) {
            onAddTaste(trimmedTaste);
            setNewTaste('');
        }
    };
    
    const handleAddPriceClick = () => {
        const trimmedPrice = newPrice.trim();
        if (trimmedPrice) {
            onAddPrice(trimmedPrice);
            setNewPrice('');
        }
    };

    const handleAddOriginClick = () => {
        const trimmedOrigin = newOrigin.trim();
        if (trimmedOrigin) {
            onAddOrigin(trimmedOrigin);
            setNewOrigin('');
        }
    };

    const handleExport = () => {
        try {
            const data: AppData = { shopProfile, wineList, occasions, tastes, prices, origins };
            const jsonString = JSON.stringify(data);
            const base64String = btoa(unescape(encodeURIComponent(jsonString)));
            setExportCode(base64String);
            setExportMessage('');
        } catch (e) {
            console.error("Export failed:", e);
            setExportCode(null);
            setExportMessage('エクスポートに失敗しました。');
        }
    };

    const handleCopyExportCode = () => {
        if (exportCode) {
            navigator.clipboard.writeText(exportCode).then(() => {
                setExportMessage('コピーしました！');
                setTimeout(() => setExportMessage(''), 2000);
            }, () => {
                setExportMessage('コピーに失敗しました。');
                setTimeout(() => setExportMessage(''), 2000);
            });
        }
    };

    const handleImport = async () => {
        setImportError(null);
        setImportSuccess(null);

        if (!importCode.trim()) {
            setImportError('インポート用のコードを入力してください。');
            return;
        }

        const isConfirmed = window.confirm(
            '現在のワインリストと設定が上書きされます。よろしいですか？この操作は元に戻せません。'
        );

        if (!isConfirmed) {
            return;
        }

        try {
            const jsonString = decodeURIComponent(escape(atob(importCode)));
            const data = JSON.parse(jsonString);
            
            const success = await onImportData(data);
            if (success) {
                setImportSuccess('データのインポートに成功しました！');
                setImportCode('');
            } else {
                 throw new Error('データの適用中にエラーが発生しました。');
            }
        } catch (error) {
            console.error("Import error:", error);
            setImportError('インポートに失敗しました。コードが破損しているか、形式が正しくありません。');
        }
    };

    const handleDownloadSampleCsv = () => {
        const header = "id,name,type,origin,variety,price,taste,characteristics\n";
        const row1 = "1,シャトー・サンプル・ルージュ,赤,フランス,カベルネ・ソーヴィニヨン,4800,フルボディ,カシス;シルキーなタンニン;エレガント\n";
        const row2 = "2,ドメーヌ・サンプル・ブラン,白,イタリア,ピノ・グリージョ,2500,辛口,青リンゴ;ミネラル;フレッシュ\n";
        const csvContent = header + row1 + row2;

        const bom = new Uint8Array([0xEF, 0xBB, 0xBF]); // For UTF-8
        const blob = new Blob([bom, csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "sample_wine_list.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleDownloadCurrentCsv = () => {
        if (wineList.length === 0) return;

        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const timestamp = `${year}${month}${day}_${hours}${minutes}${seconds}`;
        const filename = `wine_list_${timestamp}.csv`;

        const headers = "id,name,type,origin,variety,price,taste,characteristics\n";
        
        const csvRows = wineList.map(wine => {
            const characteristics = Array.isArray(wine.characteristics) ? wine.characteristics.join(';') : String(wine.characteristics || '');
            const row = [
                wine.id,
                `"${String(wine.name || '').replace(/"/g, '""')}"`,
                wine.type,
                `"${String(wine.origin || '').replace(/"/g, '""')}"`,
                `"${String(wine.variety || '').replace(/"/g, '""')}"`,
                wine.price,
                wine.taste,
                `"${String(characteristics || '').replace(/"/g, '""')}"`
            ];
            return row.join(',');
        });

        const csvContent = headers + csvRows.join('\n');

        const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
        const blob = new Blob([bom, csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };


    return (
        <div className="w-full max-w-4xl bg-white/50 backdrop-blur-sm p-4 sm:p-6 md:p-8 rounded-2xl shadow-xl border border-gray-200/50 mx-2">
            <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-800 mb-4">設定</h2>
            <p className="text-center text-gray-600 mb-6 sm:mb-8 text-sm sm:text-base">ワインリストとアンケートの選択肢を管理します。</p>

            {/* Shop Profile Section */}
            <div className="mb-8 sm:mb-10 p-4 sm:p-6 bg-gray-50/50 rounded-lg border">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-3">店舗情報の設定</h3>
                <div className="space-y-4">
                    <div>
                        <label htmlFor="shopName" className="block text-sm font-medium text-gray-700">お店の名前</label>
                        <input
                            type="text"
                            id="shopName"
                            value={shopProfile.name}
                            onChange={(e) => onUpdateShopProfile({ name: e.target.value })}
                            placeholder="例: AIソムリエ"
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 text-sm sm:text-base"
                        />
                    </div>
                    <div>
                         <label className="block text-sm font-medium text-gray-700">お店のロゴ</label>
                         <div className="mt-1 flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                             {shopProfile.logo ? (
                                <img src={shopProfile.logo} alt="ロゴプレビュー" className="h-16 w-16 sm:h-20 sm:w-20 object-contain rounded-md bg-white p-1 border" />
                             ) : (
                                <div className="h-16 w-16 sm:h-20 sm:w-20 bg-gray-100 rounded-md flex items-center justify-center text-gray-400 border">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 sm:h-8 sm:w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14" /></svg>
                                </div>
                             )}
                             <div className="flex flex-col gap-2">
                                <label htmlFor="logo-upload" className="cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50">
                                    <span>ロゴをアップロード</span>
                                    <input id="logo-upload" name="logo" type="file" className="sr-only" accept="image/*" onChange={handleLogoChange} />
                                </label>
                                {shopProfile.logo && (
                                     <button onClick={handleRemoveLogo} className="text-sm text-red-600 hover:underline text-left">
                                        ロゴを削除
                                    </button>
                                )}
                             </div>
                         </div>
                    </div>
                </div>
            </div>

            {/* Pagination Settings Section */}
            <div className="mb-8 sm:mb-10 p-4 sm:p-6 bg-gray-50/50 rounded-lg border">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-3">ワインリスト表示設定</h3>
                <p className="text-sm text-gray-600 mb-4">
                    携帯でのワインリスト表示方法を設定します。
                </p>
                <div className="space-y-4">
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="paginationEnabled"
                            checked={paginationSettings?.enabled || false}
                            onChange={(e) => onUpdatePaginationSettings({
                                ...(paginationSettings || { enabled: false, mode: 'byGenre', winesPerPage: 12 }),
                                enabled: e.target.checked
                            })}
                            className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                        />
                        <label htmlFor="paginationEnabled" className="ml-2 text-sm font-medium text-gray-700">
                            ページ分けを有効にする
                        </label>
                    </div>
                    
                    {paginationSettings?.enabled && (
                        <div className="ml-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">分け方の方法</label>
                                <div className="space-y-2">
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="paginationMode"
                                            value="byGenre"
                                            checked={paginationSettings?.mode === 'byGenre'}
                                            onChange={(e) => onUpdatePaginationSettings({
                                                ...(paginationSettings || { enabled: false, mode: 'byGenre', winesPerPage: 12 }),
                                                mode: e.target.value as 'byGenre' | 'byCount'
                                            })}
                                            className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">ジャンル別（スパークリング、赤、白、ロゼ）</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="paginationMode"
                                            value="byCount"
                                            checked={paginationSettings?.mode === 'byCount'}
                                            onChange={(e) => onUpdatePaginationSettings({
                                                ...(paginationSettings || { enabled: false, mode: 'byGenre', winesPerPage: 12 }),
                                                mode: e.target.value as 'byGenre' | 'byCount'
                                            })}
                                            className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">本数別</span>
                                    </label>
                                </div>
                            </div>
                            
                            {paginationSettings?.mode === 'byCount' && (
                                <div>
                                    <label htmlFor="winesPerPage" className="block text-sm font-medium text-gray-700">1ページあたりのワイン数</label>
                                    <input
                                        type="number"
                                        id="winesPerPage"
                                        min="1"
                                        max="50"
                                        value={paginationSettings?.winesPerPage || 12}
                                        onChange={(e) => onUpdatePaginationSettings({
                                            ...(paginationSettings || { enabled: false, mode: 'byGenre', winesPerPage: 12 }),
                                            winesPerPage: parseInt(e.target.value) || 12
                                        })}
                                        className="mt-1 block w-20 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 text-sm"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Occasion Management Section */}
            <div className="mb-8 sm:mb-10 p-4 sm:p-6 bg-gray-50/50 rounded-lg border">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-3">シーンの管理</h3>
                <p className="text-sm text-gray-600 mb-4">
                    ワインを探す際の「どんなシーンで飲みますか？」の選択肢を編集します。
                </p>
                <div className="space-y-2 mb-4 max-h-40 overflow-y-auto pr-2">
                    {occasions.filter(o => o !== '指定なし').map(occasion => (
                        <div key={occasion} className="flex items-center justify-between bg-white p-2 pl-4 rounded-md border">
                            <span className="text-gray-800 text-sm sm:text-base">{occasion}</span>
                            <button
                                onClick={() => onRemoveOccasion(occasion)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-100 rounded-full transition-colors"
                                aria-label={`「${occasion}」を削除`}
                            >
                                <TrashIcon />
                            </button>
                        </div>
                    ))}
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <input
                        type="text"
                        value={newOccasion}
                        onChange={(e) => setNewOccasion(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddOccasionClick(); }}}
                        placeholder="新しいシーンを追加"
                        className="flex-grow mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 text-sm sm:text-base"
                    />
                    <button
                        onClick={handleAddOccasionClick}
                        type="button"
                        className="bg-red-800 text-white font-bold py-2 px-4 rounded-lg shadow-sm hover:bg-red-900 transition-colors duration-300 flex-shrink-0 mt-1 text-sm sm:text-base"
                    >
                        追加
                    </button>
                </div>
            </div>

            {/* Taste Management Section */}
            <div className="mb-8 sm:mb-10 p-4 sm:p-6 bg-gray-50/50 rounded-lg border">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-3">味わいの管理</h3>
                <p className="text-sm text-gray-600 mb-4">
                    ワインを探す際の「どんな味わいがお好みですか？」の選択肢を編集します。
                </p>
                <div className="space-y-2 mb-4 max-h-40 overflow-y-auto pr-2">
                    {tastes.filter(t => t !== '指定なし').map(taste => (
                        <div key={taste} className="flex items-center justify-between bg-white p-2 pl-4 rounded-md border">
                            <span className="text-gray-800 text-sm sm:text-base">{taste}</span>
                            <button
                                onClick={() => onRemoveTaste(taste)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-100 rounded-full transition-colors"
                                aria-label={`「${taste}」を削除`}
                            >
                                <TrashIcon />
                            </button>
                        </div>
                    ))}
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <input
                        type="text"
                        value={newTaste}
                        onChange={(e) => setNewTaste(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTasteClick(); }}}
                        placeholder="新しい味わいを追加"
                        className="flex-grow mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 text-sm sm:text-base"
                    />
                    <button
                        onClick={handleAddTasteClick}
                        type="button"
                        className="bg-red-800 text-white font-bold py-2 px-4 rounded-lg shadow-sm hover:bg-red-900 transition-colors duration-300 flex-shrink-0 mt-1 text-sm sm:text-base"
                    >
                        追加
                    </button>
                </div>
            </div>
            
            {/* Price Management Section */}
            <div className="mb-8 sm:mb-10 p-4 sm:p-6 bg-gray-50/50 rounded-lg border">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-3">価格帯の管理</h3>
                <p className="text-sm text-gray-600 mb-4">
                    ワインを探す際の「ご予算は？」の選択肢を編集します。
                </p>
                <div className="space-y-2 mb-4 max-h-40 overflow-y-auto pr-2">
                    {prices.filter(p => p !== '指定なし').map(price => (
                        <div key={price} className="flex items-center justify-between bg-white p-2 pl-4 rounded-md border">
                            <span className="text-gray-800 text-sm sm:text-base">{price}</span>
                            <button
                                onClick={() => onRemovePrice(price)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-100 rounded-full transition-colors"
                                aria-label={`「${price}」を削除`}
                            >
                                <TrashIcon />
                            </button>
                        </div>
                    ))}
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <input
                        type="text"
                        value={newPrice}
                        onChange={(e) => setNewPrice(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddPriceClick(); }}}
                        placeholder="新しい価格帯を追加 (例: ¥10,001～)"
                        className="flex-grow mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:ring-red-500 focus:border-red-500 text-sm sm:text-base"
                    />
                    <button
                        onClick={handleAddPriceClick}
                        type="button"
                        className="bg-red-800 text-white font-bold py-2 px-4 rounded-lg shadow-sm hover:bg-red-900 transition-colors duration-300 flex-shrink-0 mt-1 text-sm sm:text-base"
                    >
                        追加
                    </button>
                </div>
            </div>
            
            {/* Origin Management Section */}
            <div className="mb-8 sm:mb-10 p-4 sm:p-6 bg-gray-50/50 rounded-lg border">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-3">産地の管理</h3>
                <p className="text-sm text-gray-600 mb-4">
                    ワインを探す際の「産地の希望はありますか？」の選択肢を編集します。
                </p>
                <div className="space-y-2 mb-4 max-h-40 overflow-y-auto pr-2">
                    {origins.filter(o => o !== '指定なし').map(origin => (
                        <div key={origin} className="flex items-center justify-between bg-white p-2 pl-4 rounded-md border">
                            <span className="text-gray-800 text-sm sm:text-base">{origin}</span>
                            <button
                                onClick={() => onRemoveOrigin(origin)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-100 rounded-full transition-colors"
                                aria-label={`「${origin}」を削除`}
                            >
                                <TrashIcon />
                            </button>
                        </div>
                    ))}
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <input
                        type="text"
                        value={newOrigin}
                        onChange={(e) => setNewOrigin(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddOriginClick(); }}}
                        placeholder="新しい産地を追加"
                        className="flex-grow mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 text-sm sm:text-base"
                    />
                    <button
                        onClick={handleAddOriginClick}
                        type="button"
                        className="bg-red-800 text-white font-bold py-2 px-4 rounded-lg shadow-sm hover:bg-red-900 transition-colors duration-300 flex-shrink-0 mt-1 text-sm sm:text-base"
                    >
                        追加
                    </button>
                </div>
            </div>

            {/* CSV Import Section */}
            <div className="mb-8 sm:mb-10 p-4 sm:p-6 bg-gray-50/50 rounded-lg border">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-3">CSVからインポート</h3>
                <p className="text-sm text-gray-600 mb-4">
                    ワインリストをCSVファイルで一括で上書きします。ヘッダーは `id,name,type,origin,variety,price,taste,characteristics` にしてください。
                </p>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
                    <label 
                        htmlFor="csv-upload"
                        className="cursor-pointer bg-white border border-red-800 text-red-800 font-bold py-2 px-4 rounded-lg shadow-sm hover:bg-red-50 transition-colors duration-300 inline-block text-center text-sm sm:text-base"
                    >
                        CSVファイルを選択
                    </label>
                    <input 
                        id="csv-upload" 
                        type="file" 
                        accept=".csv"
                        className="hidden"
                        onChange={onFileChange}
                    />
                    <button 
                        onClick={handleDownloadSampleCsv}
                        type="button"
                        className="text-sm text-gray-600 hover:text-red-800 hover:underline transition-colors text-center"
                    >
                        CSVのサンプルデータをダウンロード
                    </button>
                </div>
                {csvFileName && <p className="mt-3 text-green-700 font-medium text-sm sm:text-base">✓「{csvFileName}」が正常に読み込まれ、リストが更新されました。</p>}
                {csvError && <p className="mt-3 text-red-600 font-medium text-sm sm:text-base">! {csvError}</p>}
            </div>

            {/* Add Wine Form Section */}
            <div className="mb-8 sm:mb-10 p-4 sm:p-6 bg-gray-50/50 rounded-lg border">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-3">新しいワインを追加</h3>
                <AddWineForm onAddWine={onAddWine} />
            </div>

            {/* Current Wine List Section */}
            <div className="p-4 sm:p-6 bg-gray-50/50 rounded-lg border">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 gap-2">
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-700">現在のワインリスト ({wineList.length}本)</h3>
                     <button
                        onClick={handleDownloadCurrentCsv}
                        disabled={wineList.length === 0}
                        className="bg-gray-600 text-white font-bold py-2 px-4 rounded-lg shadow-sm hover:bg-gray-700 transition-colors duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
                    >
                        CSVでダウンロード
                    </button>
                </div>
                 <WineListTable 
                    wineList={wineList} 
                    onRemoveWine={onRemoveWine} 
                    onStartEdit={onStartEdit}
                    onToggleVisibility={onToggleWineVisibility} 
                 />
            </div>
            
             {/* Data Transfer Section */}
            <details className="my-8 sm:my-10 p-4 sm:p-6 bg-gray-50/50 rounded-lg border group">
                <summary className="text-lg sm:text-xl font-semibold text-gray-700 cursor-pointer list-none flex justify-between items-center">
                    データのエクスポート/インポート
                    <span className="text-gray-400 group-open:rotate-90 transition-transform duration-200">&#9656;</span>
                </summary>
                <div className="mt-4">
                    <p className="text-sm text-gray-600 mb-4">
                        現在のワインリストと設定を一つのコードに変換してバックアップしたり、他の端末に移行したりできます。
                    </p>
                    
                    {/* Export */}
                    <div className="mb-6">
                        <h4 className="font-semibold text-gray-700 mb-2 text-sm sm:text-base">1. データをエクスポート</h4>
                        <p className="text-sm text-gray-600 mb-3">
                            下のボタンを押して、現在のデータをバックアップ用のコードとして生成します。
                        </p>
                        <button onClick={handleExport} className="bg-red-800 text-white font-bold py-2 px-4 rounded-lg shadow-sm hover:bg-red-900 transition-colors duration-300 text-sm sm:text-base">
                            エクスポートコードを生成
                        </button>
                        {exportCode && (
                            <div className="mt-4 p-4 bg-white border rounded-lg">
                                <p className="text-sm text-gray-600 mb-2">以下のコードをコピーして保存してください:</p>
                                <textarea
                                    readOnly
                                    value={exportCode}
                                    className="w-full h-24 p-2 border border-gray-300 rounded-md font-mono text-xs bg-gray-50"
                                    aria-label="エクスポートコード"
                                ></textarea>
                                <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 mt-2">
                                    <button onClick={handleCopyExportCode} className="bg-gray-600 text-white text-sm font-bold py-1 px-3 rounded-md hover:bg-gray-700 transition-colors">
                                        コピー
                                    </button>
                                     <span className="text-sm text-green-700">{exportMessage}</span>
                                    <button onClick={() => setExportCode(null)} className="text-sm text-gray-500 hover:underline ml-auto">
                                        閉じる
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Import */}
                    <div>
                        <h4 className="font-semibold text-gray-700 mb-2 text-sm sm:text-base">2. データをインポート</h4>
                        <p className="text-sm text-gray-600 mb-3">
                            エクスポートしたコードを貼り付けて、データを復元します。<strong className="text-red-600">現在のデータは上書きされます。</strong>
                        </p>
                        <textarea
                            value={importCode}
                            onChange={(e) => setImportCode(e.target.value)}
                            placeholder="ここにエクスポートしたコードを貼り付けます"
                            className="w-full h-24 p-2 border border-gray-300 rounded-md font-mono text-xs focus:ring-red-500 focus:border-red-500"
                            aria-label="インポートコード入力欄"
                        ></textarea>
                        <div className="mt-3">
                            <button
                                onClick={handleImport}
                                disabled={!importCode.trim()}
                                className="bg-green-700 text-white font-bold py-2 px-4 rounded-lg shadow-sm hover:bg-green-800 transition-colors duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
                            >
                                インポートを実行
                            </button>
                        </div>
                         {importError && <p className="mt-3 text-red-600 font-medium text-sm sm:text-base">! {importError}</p>}
                         {importSuccess && <p className="mt-3 text-green-700 font-medium text-sm sm:text-base">✓ {importSuccess}</p>}
                    </div>
                </div>
            </details>


            {/* ユーザー別QRコード表示 */}
            {userId ? (
                <div className="mt-8 p-6 bg-white rounded-lg shadow-sm border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">
                        📱 あなたのワインリストQRコード
                    </h3>
                    <div className="flex justify-center">
                        <QRCodeDisplay 
                            url={(() => {
                                console.log('=== Settings 動的ワインリスト URL生成デバッグ ===');
                                console.log('userId prop:', userId);
                                console.log('userId type:', typeof userId);
                                console.log('userId length:', userId?.length);
                                
                                if (!userId) {
                                    console.error('userId が undefined または null です');
                                    return 'http://wine.orange-ark.jp';
                                }
                                
                                // 動的ワインリストのURLを生成
                                const dynamicUrl = `${window.location.origin}/public-wine-list?user=${encodeURIComponent(userId)}`;
                                console.log('動的ワインリスト URL:', dynamicUrl);
                                console.log('URL長:', dynamicUrl.length);
                                
                                return dynamicUrl;
                            })()}
                            title="顧客用ワインリスト（動的表示）" 
                        />
                    </div>
                    <p className="text-sm text-gray-600 text-center mt-4">
                        このQRコードを顧客に提供すると、あなたのワインリストに直接アクセスできます<br/>
                        <span className="text-xs text-blue-600">※動的表示のため、常に最新のワインリストが表示されます</span>
                    </p>
                    
                </div>
            ) : (
                <div className="mt-8 p-6 bg-yellow-50 rounded-lg shadow-sm border border-yellow-200">
                    <h3 className="text-lg font-semibold text-yellow-800 mb-4 text-center">
                        ⚠️ userId が設定されていません
                    </h3>
                    <p className="text-sm text-yellow-700 text-center">
                        userId: {String(userId)} (type: {typeof userId})
                    </p>
                </div>
            )}
        </div>
    );
};

export default Settings;
